import { useState } from "react";
import confetti from "canvas-confetti";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ConnectDataIcon } from "../icons/WizardModeIcons";
import { buildPriceIds, clientUsesBookings, isEarlySupporter, planSoldOnCycle } from "../pricing";
import { invokeInternal } from "../invoke";
import { isStripeTestMode } from "@/config/stripePlans";
import type { StepProps } from "../types";

// Shown to the operator after an AI-only client is set up: the agent runs in
// answer-only mode until the client's own data/actions are connected.
const AiOnlyNextStep = () => (
  <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-left">
    <div className="flex items-start gap-3">
      <ConnectDataIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="text-sm">
        <div className="font-semibold">Next: connect their data &amp; actions</div>
        <p className="text-xs text-muted-foreground mt-0.5">
          This is an AI-assistant-only client (no Dibs Bookings). In the client's
          dashboard, go to <span className="font-medium">Business data → Connections</span>{" "}
          to hook up their live data (URL feed / API), write actions, or an MCP
          server — so the AI can answer and act for them. Until then the agent
          answers from the business info you entered and hands off transactions.
        </p>
      </div>
    </div>
  </div>
);

const FinishStep = ({ data }: StepProps) => {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [freeOnboarded, setFreeOnboarded] = useState(false);
  const [copied, setCopied] = useState(false);

  const fireConfetti = () => {
    confetti({ particleCount: 140, spread: 75, origin: { y: 0.6 } });
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data: res, error } = await invokeInternal<{
        url?: string;
        free?: boolean;
        error?: string;
      }>("internal-onboarding-checkout", {
        email: data.clientEmail.toLowerCase().trim(),
        priceIds: buildPriceIds(data),
        test_mode: isStripeTestMode,
        origin: window.location.origin,
        metadata: {
          businessName: data.businessName,
          businessEmail: data.clientEmail,
          businessAddress: data.businessAddress,
          category: data.category,
          phone: data.phone,
          contactName: data.contactName,
          selectedPlan: data.selectedPlan,
          billingCycle: data.billingCycle,
          isYearly: data.billingCycle === "yearly" ? "true" : "false",
          isEarlySupporter: isEarlySupporter(data) ? "true" : "false",
          earlySupporterGrants: data.earlySupporterGrants.join(","),
        },
      });
      if (error) throw new Error(error.message);
      if (res?.error) throw new Error(res.error);
      if (res?.free) {
        setFreeOnboarded(true);
        fireConfetti();
        toast.success(
          isEarlySupporter(data)
            ? "Client onboarded on the Early Supporter Plan"
            : "Client onboarded on Free plan"
        );
        return;
      }
      if (!res?.url) throw new Error("No payment link returned");
      setUrl(res.url);
      fireConfetti();
      toast.success("Payment link generated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to generate link");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (freeOnboarded) {
    return (
      <div className="space-y-6 text-center py-6">
        <div className="flex flex-col items-center gap-2">
          <CheckCircle2 className="h-12 w-12 text-primary" />
          <h2 className="text-2xl font-heading font-semibold">
            Client onboarded
          </h2>
          <p className="text-sm text-muted-foreground">
            {data.clientEmail} is now set up
            {isEarlySupporter(data) ? " on the Early Supporter Plan" : " on the Free plan"}.
            They can sign in to the admin dashboard at any time.
          </p>
        </div>
        {!clientUsesBookings(data) && <AiOnlyNextStep />}
      </div>
    );
  }

  if (!url) {
    // No Stripe line items (Free plan, or comped Early Supporter grants) → the
    // no-payment completion path.
    // planSoldOnCycle first: buildPriceIds throws for a plan not sold on the cycle.
    const isFree = planSoldOnCycle(data) && buildPriceIds(data).length === 0;
    return (
      <div className="space-y-6 text-center py-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-heading font-semibold">
            {isFree ? "Complete onboarding" : "Generate payment link"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isFree
              ? `This saves the onboarding details to ${data.clientEmail}'s account and activates the Free plan. No payment needed.`
              : `This saves the onboarding details to ${data.clientEmail}'s account and creates a single Stripe checkout link for the plan and all add-ons.`}
          </p>
        </div>
        <Button size="lg" onClick={generate} disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {isFree ? "Complete Free onboarding" : "Generate Stripe payment link"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-center py-4">
      <div className="flex flex-col items-center gap-2">
        <CheckCircle2 className="h-12 w-12 text-primary" />
        <h2 className="text-2xl font-heading font-semibold">All set!</h2>
        <p className="text-sm text-muted-foreground">
          Share this link or QR code with the client to complete payment.
        </p>
      </div>

      <div className="flex justify-center">
        <div className="rounded-xl border bg-card p-4">
          <QRCodeSVG value={url} size={180} />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
        <code className="text-xs bg-muted px-3 py-2 rounded-md max-w-full truncate">
          {url}
        </code>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copy}>
            {copied ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(url, "_blank")}
          >
            <ExternalLink className="h-4 w-4" />
            Open
          </Button>
        </div>
      </div>

      {!clientUsesBookings(data) && <AiOnlyNextStep />}
    </div>
  );
};

export default FinishStep;
